import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { CalendarCheck2, Loader2, Play, CheckCircle2, AlertTriangle, FileWarning, ExternalLink } from "lucide-react";

const fmtD = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "—");
const fmtDT = (iso) => { try { return new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; } };
const STATUS = {
    diff: { label: "Da correggere", cls: "bg-amber-100 text-amber-800" },
    ok: { label: "Allineata", cls: "bg-emerald-50 text-emerald-700" },
    no_payslip: { label: "Senza busta paga", cls: "bg-slate-100 text-slate-500" },
    no_hire_date: { label: "Senza data (collab.)", cls: "bg-slate-100 text-slate-500" },
    parse_error: { label: "Cedolino non leggibile", cls: "bg-red-50 text-red-600" },
    cf_mismatch: { label: "CF diverso!", cls: "bg-red-100 text-red-700" },
};
const FILTERS = [["diff", "Differenze"], ["anomalie", "Anomalie"], ["all", "Tutti"]];

const Kpi = ({ label, value, cls, testId }) => (
    <div className={`rounded-lg border px-4 py-3 ${cls}`} data-testid={testId}>
        <div className="text-[10px] font-bold uppercase tracking-wide opacity-70">{label}</div>
        <div className="font-display text-xl font-bold">{value}</div>
    </div>
);

export default function HireDateCheckPage() {
    const [result, setResult] = useState(null);
    const [job, setJob] = useState(null);
    const [filter, setFilter] = useState("diff");
    const [selected, setSelected] = useState([]);
    const [applying, setApplying] = useState(false);
    const timer = useRef(null);

    const loadLatest = useCallback(() => api.get("/hire-dates/latest").then((r) => setResult(r.data)).catch(() => {}), []);
    useEffect(() => { loadLatest(); return () => clearInterval(timer.current); }, [loadLatest]);

    const poll = useCallback((jobId) => {
        clearInterval(timer.current);
        timer.current = setInterval(async () => {
            try {
                const { data } = await api.get(`/hire-dates/scan/${jobId}`);
                setJob(data);
                if (data.status !== "running") {
                    clearInterval(timer.current);
                    if (data.status === "done") { toast.success("Verifica completata"); await loadLatest(); setSelected([]); }
                    else toast.error(data.error || "Verifica interrotta");
                }
            } catch { clearInterval(timer.current); setJob(null); }
        }, 1500);
    }, [loadLatest]);

    const start = async () => {
        try {
            const { data } = await api.post("/hire-dates/scan");
            setJob({ id: data.job_id, status: "running", total: 0, processed: 0 });
            poll(data.job_id);
        } catch { toast.error("Impossibile avviare la verifica"); }
    };

    const rows = useMemo(() => result?.rows || [], [result]);
    const diffs = useMemo(() => rows.filter((r) => r.status === "diff"), [rows]);
    const visible = useMemo(() => {
        if (filter === "diff") return diffs;
        if (filter === "anomalie") return rows.filter((r) => ["cf_mismatch", "parse_error", "no_payslip"].includes(r.status) || r.nascita_diff || r.contract_mismatch);
        return rows;
    }, [rows, diffs, filter]);

    const apply = async (ids) => {
        if (!ids.length) return;
        if (!window.confirm(`Allineare la data di assunzione di ${ids.length} dipendent${ids.length === 1 ? "e" : "i"} alla busta paga (anche nella sezione Contratti)?`)) return;
        setApplying(true);
        try {
            const { data } = await api.post("/hire-dates/apply", { employee_ids: ids });
            toast.success(`${data.applied.length} dat${data.applied.length === 1 ? "a corretta" : "e corrette"}${data.skipped.length ? ` · ${data.skipped.length} saltate` : ""}`);
            setSelected([]);
            await loadLatest();
        } catch (e) {
            const d = e.response?.data?.detail; toast.error(typeof d === "string" ? d : "Errore nella correzione");
        } finally { setApplying(false); }
    };

    const running = job?.status === "running";
    const s = result?.summary || {};

    return (
        <div className="mx-auto max-w-7xl px-6 py-8" data-testid="hire-date-check-page">
            <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]"><CalendarCheck2 className="h-6 w-6" /></div>
                    <div>
                        <h1 className="font-display text-2xl font-bold text-slate-800">Verifica date di assunzione</h1>
                        <p className="text-sm text-slate-500">Confronta la data di assunzione in archivio con quella stampata sull'ultima busta paga di ogni dipendente (fonte certa) e allinea archivio e sezione Contratti.</p>
                    </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                    <Button onClick={start} disabled={running} className="gap-2 bg-[#376DB3] text-white hover:bg-[#2c5a96]" data-testid="hdc-start-btn">
                        {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} {result ? "Ripeti verifica" : "Avvia verifica"}
                    </Button>
                    {result?.finished_at && <span className="text-[11px] text-slate-400" data-testid="hdc-last-run">Ultima verifica: {fmtDT(result.finished_at)}</span>}
                </div>
            </div>

            {running && (
                <div className="mb-5 rounded-lg border border-slate-200 bg-white p-4 shadow-sm" data-testid="hdc-progress">
                    <div className="mb-1 flex justify-between text-xs text-slate-600"><span>Lettura cedolini in corso…</span><span className="font-mono-numbers">{job.processed}/{job.total || "…"}</span></div>
                    <div className="h-2 overflow-hidden rounded-full bg-slate-100"><div className="h-full bg-[#376DB3] transition-all" style={{ width: job.total ? `${(job.processed / job.total) * 100}%` : "5%" }} /></div>
                </div>
            )}

            {result && (
                <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-6" data-testid="hdc-kpis">
                    <Kpi label="Verificati" value={result.total} cls="border-slate-200 bg-white text-slate-800" testId="kpi-total" />
                    <Kpi label="Allineate" value={s.ok || 0} cls="border-emerald-200 bg-emerald-50 text-emerald-800" testId="kpi-ok" />
                    <Kpi label="Da correggere" value={s.diff || 0} cls="border-amber-200 bg-amber-50 text-amber-800" testId="kpi-diff" />
                    <Kpi label="Contratti disallineati" value={s.contract_mismatch || 0} cls="border-amber-200 bg-white text-amber-800" testId="kpi-contract" />
                    <Kpi label="Senza busta / collab." value={(s.no_payslip || 0) + (s.no_hire_date || 0)} cls="border-slate-200 bg-white text-slate-600" testId="kpi-nopayslip" />
                    <Kpi label="Anomalie CF / lettura" value={(s.cf_mismatch || 0) + (s.parse_error || 0)} cls="border-red-200 bg-red-50 text-red-700" testId="kpi-anomalie" />
                </div>
            )}

            {!result && !running && (
                <div className="rounded-lg border border-dashed border-slate-200 bg-white px-6 py-12 text-center text-sm text-slate-500" data-testid="hdc-empty">
                    Nessuna verifica eseguita. Premi <b>Avvia verifica</b>: il portale legge l'ultima busta paga di ogni dipendente (circa 1-2 minuti).
                </div>
            )}

            {result && (
                <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
                        <div className="flex gap-1 rounded-md bg-slate-100 p-0.5">
                            {FILTERS.map(([k, l]) => (
                                <button key={k} type="button" onClick={() => setFilter(k)} className={`rounded px-3 py-1 text-xs font-semibold ${filter === k ? "bg-white text-slate-800 shadow-sm" : "text-slate-500"}`} data-testid={`hdc-filter-${k}`}>
                                    {l}{k === "diff" ? ` (${diffs.length})` : ""}
                                </button>
                            ))}
                        </div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" disabled={!selected.length || applying} onClick={() => apply(selected)} className="gap-1.5 text-xs" data-testid="hdc-apply-selected">
                                <CheckCircle2 className="h-3.5 w-3.5" /> Correggi selezionati ({selected.length})
                            </Button>
                            <Button size="sm" disabled={!diffs.length || applying} onClick={() => apply(diffs.map((r) => r.employee_id))} className="gap-1.5 bg-amber-600 text-xs text-white hover:bg-amber-700" data-testid="hdc-apply-all">
                                {applying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Correggi tutte le differenze ({diffs.length})
                            </Button>
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[1000px] text-sm" data-testid="hdc-table">
                            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                                <tr>
                                    <th className="w-8 px-3 py-2">
                                        <input type="checkbox" className="h-3.5 w-3.5 accent-[#376DB3]" checked={diffs.length > 0 && selected.length === diffs.length} onChange={(e) => setSelected(e.target.checked ? diffs.map((r) => r.employee_id) : [])} data-testid="hdc-select-all" />
                                    </th>
                                    <th className="px-3 py-2">Dipendente</th><th className="px-3 py-2">Busta paga</th>
                                    <th className="px-3 py-2">Assunzione archivio</th><th className="px-3 py-2">Assunzione busta</th>
                                    <th className="px-3 py-2">Contratti (decorrenza)</th><th className="px-3 py-2">UNILAV inizio</th><th className="px-3 py-2">Stato</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visible.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-slate-400">Nessuna riga per questo filtro{filter === "diff" ? ": tutte le date sono allineate" : ""}.</td></tr>}
                                {visible.map((r) => {
                                    const st = STATUS[r.status] || STATUS.ok;
                                    const isDiff = r.status === "diff";
                                    return (
                                        <tr key={r.employee_id} className={`border-t border-slate-100 ${isDiff ? "bg-amber-50/40" : ""}`} data-testid={`hdc-row-${r.employee_id}`}>
                                            <td className="px-3 py-2">{isDiff && <input type="checkbox" className="h-3.5 w-3.5 accent-[#376DB3]" checked={selected.includes(r.employee_id)} onChange={() => setSelected((p) => (p.includes(r.employee_id) ? p.filter((x) => x !== r.employee_id) : [...p, r.employee_id]))} data-testid={`hdc-select-${r.employee_id}`} />}</td>
                                            <td className="px-3 py-2">
                                                <Link to={`/dipendenti/${r.employee_id}`} className="font-semibold text-slate-700 hover:text-[#376DB3]">{r.cognome} {r.nome}</Link>
                                                {r.matricola && <span className="ml-1 text-xs text-slate-400">· {r.matricola}</span>}
                                                {r.nascita_diff && <div className="mt-0.5 flex items-center gap-1 text-[11px] text-red-600"><AlertTriangle className="h-3 w-3" /> Data di nascita diversa: archivio {fmtD(r.db_nascita)} · busta {fmtD(r.busta_nascita)}</div>}
                                                {r.detail && <div className="mt-0.5 text-[11px] text-slate-500">{r.detail}</div>}
                                            </td>
                                            <td className="px-3 py-2 text-xs text-slate-600">
                                                {r.period ? <a href={`/dipendenti/${r.employee_id}?tab=documenti`} className="inline-flex items-center gap-1 hover:text-[#376DB3]">{r.period}<ExternalLink className="h-3 w-3" /></a> : <span className="text-slate-300">—</span>}
                                            </td>
                                            <td className={`px-3 py-2 font-mono-numbers ${isDiff ? "text-red-600 line-through" : "text-slate-700"}`}>{fmtD(r.db_assunzione)}</td>
                                            <td className={`px-3 py-2 font-mono-numbers font-semibold ${isDiff ? "text-emerald-700" : "text-slate-700"}`}>{fmtD(r.busta_assunzione)}</td>
                                            <td className={`px-3 py-2 font-mono-numbers text-xs ${r.contract_mismatch ? "font-semibold text-amber-700" : "text-slate-500"}`}>{r.contract_decorrenza ? fmtD(r.contract_decorrenza) : <span className="text-slate-300">—</span>}{r.contract_mismatch && <FileWarning className="ml-1 inline h-3 w-3" />}</td>
                                            <td className={`px-3 py-2 font-mono-numbers text-xs ${r.unilav_inizio && r.busta_assunzione && r.unilav_inizio === r.busta_assunzione ? "text-emerald-700" : "text-slate-500"}`}>{r.unilav_inizio ? fmtD(r.unilav_inizio) : <span className="text-slate-300">—</span>}</td>
                                            <td className="px-3 py-2"><span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${st.cls}`} data-testid={`hdc-status-${r.employee_id}`}>{st.label}</span>{r.applied_at && <div className="text-[10px] text-slate-400">corretta {fmtDT(r.applied_at)}</div>}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
